import {Component, input, signal} from '@angular/core';
import {FormControl, ReactiveFormsModule} from '@angular/forms';

import {FieldConfig} from '../../../models/form-config.model';
import {CustomInput} from '../../../../../widgets/custom-input/custom-input';

/**
 * PasswordField — campo password basato su CustomInput con toggle mostra/nascondi.
 *
 * Riusa il widget CustomInput esistente (stesso look & feel degli altri campi)
 * cambiando dinamicamente il `type` tra 'password' e 'text'. Il pulsante occhio
 * è sovrapposto in fondo al campo.
 *
 * `autocomplete="new-password"` è necessario, non decorativo: senza, i gestori
 * di password del browser riempiono questi campi con le credenziali di accesso
 * al gestionale. Nelle schede in modifica — utente di studio, credenziali AdE —
 * la password è facoltativa e vuota significa "lascia quella registrata":
 * un riempimento automatico la sostituirebbe in silenzio con quella sbagliata.
 * `autocomplete="off"` non basta, i browser lo ignorano sui campi password.
 */
@Component({
  selector: 'app-password-field',
  standalone: true,
  imports: [ReactiveFormsModule, CustomInput],
  template: `
    <div class="password-field">
      <app-custom-input
        [label]="field().label"
        [type]="revealed() ? 'text' : 'password'"
        autocomplete="new-password"
        [showIcon]="!!field().icon"
        [iconSource]="field().icon ?? ''"
        [placeholder]="' '"
        [control]="control()"
        [required]="field().required ?? false"
        [class.has-error]="control().invalid && control().touched"
      />
      <button
        type="button"
        class="password-field__toggle"
        [attr.aria-label]="revealed() ? 'Nascondi password' : 'Mostra password'"
        [attr.aria-pressed]="revealed()"
        (click)="toggle()"
      >
        @if (revealed()) {
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round" width="18" height="18" aria-hidden="true">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
          </svg>
        } @else {
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round" stroke-linejoin="round" width="18" height="18" aria-hidden="true">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        }
      </button>
    </div>
  `,
  styles: [`
    :host { display: block; width: 100%; }
    .password-field { position: relative; width: 100%; }
    .password-field__toggle {
      position: absolute;
      top: 50%;
      right: 12px;
      transform: translateY(-50%);
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 4px;
      background: transparent;
      border: none;
      cursor: pointer;
      color: var(--text-secondary, #6b7280);
      line-height: 0;
    }
    .password-field__toggle:hover { color: var(--text-primary, #111827); }
  `],
})
export class PasswordFieldComponent {
  field = input.required<FieldConfig>();
  control = input.required<FormControl>();

  readonly revealed = signal(false);

  toggle(): void {
    this.revealed.update(v => !v);
  }
}
